import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTruck, FaBatteryFull, FaKey, FaTools, FaClock, FaShieldAlt, FaPhone } from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'towing',
      icon: <FaTruck />,
      title: 'Professional Towing Services',
      shortDesc: 'Reliable towing when you need it most',
      fullDesc: `When it comes to unexpected car trouble, a great towing service can make all the difference. Whether you're stuck on the side of the road with a flat tire or in need of a jump-start, Toronto Roadside Assistance can provide the assistance you need to get back on the road quickly and safely.

      Our professional towing services are available 24/7, utilizing state-of-the-art equipment and trained technicians who handle your vehicle with the utmost care. We understand that your vehicle is important to you, and we treat it as if it were our own.`,
      features: [
        'Flatbed and wheel-lift towing',
        'Long-distance towing available',
        'Damage-free vehicle transport',
        '24/7 emergency service',
        'GPS-tracked tow trucks',
        'Fully insured and licensed'
      ]
    },
    {
      id: 'battery',
      icon: <FaBatteryFull />,
      title: 'Battery Boost Service',
      shortDesc: 'Get your car running in minutes',
      fullDesc: `If you've ever found yourself with a dead car battery, you know how frustrating and inconvenient it can be. That's why Toronto Roadside Assistance can be a lifesaver. With the right professionals on your side, you can get your car up and running in no time.

      Toronto Roadside Assistance offers more than just a jump-start. They have the expertise to diagnose the underlying issue, whether it's a faulty alternator or a dead battery, and provide the appropriate solution. This can save you time, money, and hassle in the long run.`,
      features: [
        'Quick response time',
        'Professional battery testing',
        'Battery replacement available',
        'Alternator diagnostics',
        'Mobile service - we come to you',
        'Experienced technicians'
      ]
    },
    {
      id: 'lockout',
      icon: <FaKey />,
      title: 'Lock Out Service',
      shortDesc: 'Back in your car quickly and safely',
      fullDesc: `Getting locked out of your car can be a frustrating and stressful experience. Fortunately, Toronto Roadside Assistance can help you get back on the road quickly and easily. With the right professionals on your side, you can have your car unlocked in no time.

      Toronto Roadside Assistance offers more than just a quick fix. They provide skilled technicians who have the experience and expertise to handle any type of lockout situation. Whether you've lost your keys, locked them in your car, or broken your key in the lock, Toronto Roadside Assistance can provide the support you need to get back into your vehicle.

      Speed and reliability are two of the most important factors to consider when choosing a lock out service. Toronto Roadside Assistance understands that a lockout can be a major inconvenience, and they make it a priority to provide fast and efficient service. Whether you're stranded in a parking lot or on the side of the road, we can come to your location and get you back on the road quickly.`,
      features: [
        'Non-destructive entry methods',
        'All vehicle makes and models',
        'Fast response time',
        'Available 24/7',
        'Professional locksmith tools',
        'No damage guarantee'
      ]
    },
    {
      id: 'tire',
      icon: <FaTools />,
      title: 'Emergency Tire Change',
      shortDesc: 'Professional tire change service',
      fullDesc: `A flat tire can happen at any time and can be a major inconvenience, especially when you're on the road. That's why Toronto Roadside Assistance with the right professionals on your side, you can have your tire changed quickly and safely, so you can get back on the road with confidence.

      Toronto Roadside Assistance offers more than just a tire change. They have the experience and expertise to diagnose the underlying issue, whether it's a flat tire, a damaged tire, or a faulty tire valve. This can save you time and money in the long run by ensuring that the issue is resolved correctly the first time.`,
      features: [
        'Quick tire changes on-site',
        'Spare tire installation',
        'Tire inspection and assessment',
        'Safe removal and installation',
        'Professional equipment',
        'Available anytime, anywhere'
      ]
    }
  ];

  return (
    <div className="services">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-overlay"></div>
        <div className="container">
          <motion.div
            className="services-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="page-title">Our Services</h1>
            <p className="page-subtitle">
              Comprehensive roadside assistance solutions to get you back on the road safely and quickly
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section services-overview">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">What We Offer</h2>
            <p className="section-subtitle">
              Professional roadside assistance services available 24/7 throughout Toronto
            </p>
          </motion.div>

          <div className="services-quick-grid">
            {services.map((service, index) => (
              <motion.a
                key={index}
                href={`#${service.id}`}
                className="quick-service-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="quick-service-icon">{service.icon}</div>
                <h3 className="quick-service-title">{service.title}</h3>
                <p className="quick-service-desc">{service.shortDesc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      {services.map((service, index) => (
        <section
          key={index}
          id={service.id}
          className={`section service-detail ${index % 2 === 1 ? 'section-dark' : ''}`}
        >
          <div className="container">
            <motion.div
              className="service-detail-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="service-detail-header">
                <div className="service-detail-icon">{service.icon}</div>
                <h2 className="service-detail-title">{service.title}</h2>
              </div>

              <div className="service-detail-grid">
                <div className="service-detail-description">
                  {service.fullDesc.split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph.trim()}</p>
                  ))}
                </div>

                <div className="service-detail-features">
                  <h3 className="features-title">Key Features:</h3>
                  <ul className="features-list">
                    {service.features.map((feature, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: idx * 0.1 }}
                      >
                        {feature}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Why Choose Us Section */}
      <section className="section why-choose-us">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Why Choose Toronto Roadside Assistance?</h2>
            <p className="section-subtitle">
              Experience the difference of professional, reliable roadside service
            </p>
          </motion.div>

          <div className="why-grid">
            <motion.div
              className="why-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <FaClock className="why-icon" />
              <h3 className="why-title">Fast Response</h3>
              <p className="why-description">
                We understand urgency. Our team arrives quickly to get you back on your way.
              </p>
            </motion.div>

            <motion.div
              className="why-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <FaShieldAlt className="why-icon" />
              <h3 className="why-title">Trusted & Insured</h3>
              <p className="why-description">
                Fully licensed and insured for your complete peace of mind and protection.
              </p>
            </motion.div>

            <motion.div
              className="why-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              <FaTools className="why-icon" />
              <h3 className="why-title">Expert Technicians</h3>
              <p className="why-description">
                Our skilled professionals have years of experience handling all types of roadside emergencies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-dark services-cta">
        <div className="container">
          <motion.div
            className="services-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="services-cta-title">Need Roadside Assistance Now?</h2>
            <p className="services-cta-text">
              Don't wait in distress. Call us now and let our experts help you get back on the road.
            </p>
            <div className="services-cta-buttons">
              <a href="tel:6478988242" className="btn btn-primary btn-large">
                <FaPhone /> Call: (647) 898-8242
              </a>
              <Link to="/contact" className="btn btn-secondary btn-large">
                Contact Us Online
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;

